import { convertToRaw } from 'draft-js'
import draftToHtml from 'draftjs-to-html'
import i18next from 'i18next'
import { HttpResult, loadingAsyncState } from 'widgets-for-react'
import {
  parseResponse,
  tryParseJsonResponse,
} from '../../../../utils/parseResponse'
import { TypeCastingValidator, canSafelyCast } from '../../../../utils/safe-type-casting'
import { getParsedComponentData } from '../componentDetail/componentDetailApi'
import {
  ComponentFields,
  initialComponentEditorState,
} from './componentEditState'

export const loadComponentEditState = (componentId: number) =>
  loadingAsyncState(() =>
    getParsedComponentData(componentId).then((result) =>
      result.kind === 'result'
        ? {
            kind: 'result',
            status: result.status,
            value: initialComponentEditorState(result.value),
          }
        : result
    )
  )

const transformComponentFields = (
  component: ComponentFields,
  draft: boolean
) => ({
  Title: component.name,
  Techniques: component.techniques.map((t) => t.id),
  Appearances: component.appearances.map((t) => t.id),
  Textures: component.textures.map((t) => t.id),
  Introduction: draftToHtml(
    convertToRaw(component.introduction.getCurrentContent())
  ),
  Preparation: draftToHtml(
    convertToRaw(component.preparation.getCurrentContent())
  ),
  ServingSuggestions: draftToHtml(
    convertToRaw(component.servingSuggestions.getCurrentContent())
  ),
  WorldFlavours: component.worldFlavours.map((wf) => wf.id),
  Draft: draft,
  Ingredients: component.ingredients.map((ingredient, index) => ({
    Ingredient: ingredient.ingredient.id,
    Amount: ingredient.amount,
    ItemOrder: index,
    UnitOfMeasurement: ingredient.unitOfMeasurement.id,
    Prepreparations: ingredient.prepreparations.map((pp) => pp.id),
  })),
  MainIngredients: component.mainIngredients.map((ingr) => ingr.id),
})

const deleteComponent = async (id: number): Promise<HttpResult<any>> => {
  const headers = new Headers()
  headers.append('content-type', 'application/json')
  const res = fetch(`/api/v1/own-components/delete/${id}`, {
    method: 'post',
    headers: headers,
  })
  return parseResponse(res)
}

export const loadDeleteComponent = (id: number) =>
  loadingAsyncState(() => deleteComponent(id))

const editComponent = async (
  component: ComponentFields,
  draft: boolean,
  id: number
): Promise<HttpResult<any>> => {
  const headers = new Headers()
  headers.append('content-type', 'application/json')
  const res = fetch(`/api/v1/own-components/edit/${id}`, {
    method: 'post',
    headers: headers,
    body: JSON.stringify(transformComponentFields(component, draft)),
  })
  return parseResponse(res)
}

export const loadEditComponent = (
  component: ComponentFields,
  draft: boolean,
  id: number
) => loadingAsyncState(() => editComponent(component, draft, id))

const createComponent = async (
  component: ComponentFields,
  draft: boolean,
  parentComponentId: number
): Promise<HttpResult<any>> => {
  const headers = new Headers()
  headers.append('content-type', 'application/json')
  const res = fetch(
    `/api/v1/own-components/create?language=${i18next.language}`,
    {
      method: 'post',
      headers: headers,
      body: JSON.stringify({
        ...transformComponentFields(component, draft),
        ParentComponentId: parentComponentId,
      }),
    }
  )
  return tryParseJsonResponse(res)
}

export const loadCreateComponent = (
  component: ComponentFields,
  draft: boolean,
  parentComponentId: number
) =>
  loadingAsyncState(() =>
    createComponent(component, draft, parentComponentId).then((result) =>
      result.kind === 'result'
        ? typeof result.value === 'number'
          ? { kind: 'result', status: result.status, value: result.value }
          : { kind: 'failed', status: result.status }
        : result
    )
  )

const getData = async (): Promise<HttpResult<any>> => {
  const headers = new Headers()
  headers.append('content-type', 'application/json')
  const res = fetch(
    `/api/v1/own-components/data?language=${i18next.language}`,
    { headers: headers }
  )
  return tryParseJsonResponse(res)
}

type ApiDataItems = { Id: number; Translation: string }[]

const dataItemsValidator: TypeCastingValidator<ApiDataItems> = {
  kind: 'array',
  elementValidator: {
    kind: 'object',
    fieldsValidator: {
      Id: { kind: 'number' },
      Translation: { kind: 'string' },
    },
  },
}

type ApiData = {
  WorldFlavours: ApiDataItems
  Techniques: ApiDataItems
  Appearances: ApiDataItems
  Textures: ApiDataItems
  Prepreparations: ApiDataItems
  UnitsOfMeasurement: ApiDataItems
}

const dataValidator: TypeCastingValidator<ApiData> = {
  kind: 'object',
  fieldsValidator: {
    WorldFlavours: dataItemsValidator,
    Techniques: dataItemsValidator,
    Appearances: dataItemsValidator,
    Textures: dataItemsValidator,
    Prepreparations: dataItemsValidator,
    UnitsOfMeasurement: dataItemsValidator,
  },
}

export const loadData = loadingAsyncState(() =>
  getData().then((result) => {
    if (result.kind === 'result') {
      const maybeData = canSafelyCast(dataValidator, result.value)
      if (maybeData.kind === 'r') {
        const {
          Techniques,
          Appearances,
          Textures,
          WorldFlavours,
          Prepreparations,
          UnitsOfMeasurement,
        } = maybeData.v
        return maybeData.kind === 'r'
          ? {
              kind: 'result',
              status: result.status,
              value: {
                techniques: Techniques.map((item) => ({
                  id: item.Id,
                  translation: item.Translation,
                })),
                appearances: Appearances.map((item) => ({
                  id: item.Id,
                  translation: item.Translation,
                })),
                textures: Textures.map((item) => ({
                  id: item.Id,
                  translation: item.Translation,
                })),
                worldFlavours: WorldFlavours.map((item) => ({
                  id: item.Id,
                  translation: item.Translation,
                })),
                prepreparations: Prepreparations.map((item) => ({
                  id: item.Id,
                  translation: item.Translation,
                })),
                unitsOfMeasurement: UnitsOfMeasurement.map((item) => ({
                  id: item.Id,
                  translation: item.Translation,
                })),
              },
            }
          : { kind: 'failed', status: result.status }
      }
    }
    return result
  })
)

export const getMainIngredientsSearchResults = async (
  term: string
): Promise<HttpResult<any>> => {
  const headers = new Headers()
  headers.append('content-type', 'application/json')
  const res = fetch(
    `/api/v1/own-components/main-ingredients?language=${i18next.language}&query=${term}`,
    { headers: headers }
  )
  return tryParseJsonResponse(res)
}

export const getIngredientsSearchResults = async (
  term: string
): Promise<HttpResult<any>> => {
  const headers = new Headers()
  headers.append('content-type', 'application/json')
  const res = fetch(
    `/api/v1/own-components/ingredients?language=${i18next.language}&query=${term}`,
    { headers: headers }
  )
  return tryParseJsonResponse(res)
}

export const loadDataItems = (apicall: () => Promise<HttpResult<any>>) =>
  apicall().then((result) => {
    if (result.kind === 'result') {
      const maybeData = canSafelyCast<ApiDataItems>(
        dataItemsValidator,
        result.value
      )
      return maybeData.kind === 'r'
        ? {
            kind: 'result',
            status: result.status,
            value: maybeData.v.map((item) => ({
              id: item.Id,
              translation: item.Translation,
            })),
          }
        : { kind: 'failed', status: result.status }
    }
    return result
  })

export const loadDataItemsAsync = (apicall: () => Promise<HttpResult<any>>) =>
  loadingAsyncState(() =>
    apicall().then((result) => {
      if (result.kind === 'result') {
        const maybeData = canSafelyCast<ApiDataItems>(
          dataItemsValidator,
          result.value
        )
        return maybeData.kind === 'r'
          ? {
              kind: 'result',
              status: result.status,
              value: maybeData.v.map((item) => ({
                id: item.Id,
                translation: item.Translation,
              })),
            }
          : { kind: 'failed', status: result.status }
      }
      return result
    })
  )
